/**
 * React Query hooks for the WS-2 jobs and tour sheets (fieldiq-api#1282).
 *
 * Same contract as useWellServicing: every key starts ['wellServicing'], every
 * write invalidates that prefix, every read spreads LOAD_FAILURE_QUERY_OPTIONS.
 * Writes that can warn (a BOP stack due soon, an hour meter below its last
 * reading) return the warnings beside the data rather than dropping them.
 *
 * The /field hooks keep the API's snake_case: that surface is snake_case on
 * purpose and the screens read it as sent.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWs, unwrapWs, unwrapWsList } from './useWellServicing';
import type {
  WsBopWarning,
  WsDeleted,
  WsFieldMeta,
  WsFieldMeterWarning,
  WsFieldMyTours,
  WsFieldTour,
  WsJob,
  WsJobDetail,
  WsJobEquipment,
  WsJobEquipmentWrite,
  WsJobWrite,
  WsMeta,
  WsMeterWarning,
  WsRentIn,
  WsRentInWrite,
  WsTour,
  WsTourSummary,
  WsTourWrite,
} from '../types/well-servicing';

export interface WsJobFilters {
  status?: string;
  customerId?: string;
  wellId?: string;
  rigId?: string;
  from?: string;
  to?: string;
  q?: string;
}

const clean = (p?: object) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = String(v);
  return Object.keys(out).length ? out : undefined;
};

export const wsJobKeys = {
  jobs: (p?: WsJobFilters) => ['wellServicing', 'jobs', clean(p) ?? {}] as const,
  job: (id: string) => ['wellServicing', 'job', id] as const,
  rentIns: (jobId: string) => ['wellServicing', 'rentIns', jobId] as const,
  tours: (jobId: string) => ['wellServicing', 'tours', jobId] as const,
  tour: (id: string) => ['wellServicing', 'tour', id] as const,
  fieldMyTours: () => ['wellServicing', 'field', 'myTours'] as const,
  fieldTour: (id: string) => ['wellServicing', 'field', 'tour', id] as const,
};

export interface WsWriteResult<T, W> {
  data: T | null;
  meta: WsMeta;
  warnings: W[];
}

/** unwrapWs, keeping the write's warnings[] (absent → []). */
export function unwrapWsWrite<T, W>(body: unknown): WsWriteResult<T, W> {
  const { data, meta } = unwrapWs<T>(body);
  const w = body && typeof body === 'object' && !Array.isArray(body) ? (body as { warnings?: unknown }).warnings : undefined;
  return { data, meta, warnings: Array.isArray(w) ? (w as W[]) : [] };
}

function useWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWs(qc) });
}

// ─── Jobs ────────────────────────────────────────────────────────────────────

export function useWsJobs(filters?: WsJobFilters) {
  return useQuery({
    queryKey: wsJobKeys.jobs(filters),
    queryFn: async () => unwrapWsList<WsJob>((await api.wellServicing.jobs.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsJob(id: string | undefined) {
  return useQuery({
    queryKey: wsJobKeys.job(id ?? ''),
    queryFn: async () => unwrapWs<WsJobDetail>((await api.wellServicing.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsJob = () =>
  useWrite(async (body: WsJobWrite) => unwrapWs<WsJobDetail>((await api.wellServicing.jobs.create(body)).data));
export const useUpdateWsJob = () =>
  useWrite(async ({ id, body }: { id: string; body: WsJobWrite }) =>
    unwrapWsWrite<WsJobDetail, unknown>((await api.wellServicing.jobs.update(id, body)).data));
export const useDeleteWsJob = () =>
  useWrite(async (id: string) => (await api.wellServicing.jobs.remove(id)).data as unknown as WsDeleted);
export const useSetWsBopStack = () =>
  useWrite(async ({ id, equipmentId }: { id: string; equipmentId: string }) =>
    unwrapWsWrite<WsJobDetail, WsBopWarning>((await api.wellServicing.jobs.setBopStack(id, equipmentId)).data));
export const useClearWsBopStack = () =>
  useWrite(async (id: string) => unwrapWs<WsJobDetail>((await api.wellServicing.jobs.clearBopStack(id)).data));

export const useAddWsJobEquipment = () =>
  useWrite(async ({ jobId, body }: { jobId: string; body: WsJobEquipmentWrite }) =>
    unwrapWs<WsJobEquipment>((await api.wellServicing.jobEquipment.add(jobId, body)).data));
export const useUpdateWsJobEquipment = () =>
  useWrite(async ({ id, body }: { id: string; body: WsJobEquipmentWrite }) =>
    unwrapWs<WsJobEquipment>((await api.wellServicing.jobEquipment.update(id, body)).data));
export const useRemoveWsJobEquipment = () =>
  useWrite(async (id: string) => (await api.wellServicing.jobEquipment.remove(id)).data);

/**
 * The job's rent-ins through their own route. Used only for WS_RENT_IN_READ:
 * the job detail already carries rentIns for everyone, with the cost keys only
 * for the roles that may see them.
 */
export function useWsRentIns(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wsJobKeys.rentIns(jobId ?? ''),
    queryFn: async () => unwrapWsList<WsRentIn>((await api.wellServicing.rentIns.list(jobId as string)).data),
    enabled: !!jobId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsRentIn = () =>
  useWrite(async ({ jobId, body }: { jobId: string; body: WsRentInWrite }) =>
    unwrapWs<WsRentIn>((await api.wellServicing.rentIns.create(jobId, body)).data));
export const useUpdateWsRentIn = () =>
  useWrite(async ({ id, body }: { id: string; body: WsRentInWrite }) =>
    unwrapWs<WsRentIn>((await api.wellServicing.rentIns.update(id, body)).data));
export const useRemoveWsRentIn = () => useWrite(async (id: string) => (await api.wellServicing.rentIns.remove(id)).data);

// ─── Tour sheets (office) ────────────────────────────────────────────────────

export function useWsTours(jobId: string | undefined) {
  return useQuery({
    queryKey: wsJobKeys.tours(jobId ?? ''),
    queryFn: async () => unwrapWsList<WsTourSummary>((await api.wellServicing.tours.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsTour(id: string | undefined) {
  return useQuery({
    queryKey: wsJobKeys.tour(id ?? ''),
    queryFn: async () => unwrapWs<WsTour>((await api.wellServicing.tours.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useOpenWsTour = () =>
  useWrite(async ({ jobId, body }: { jobId: string; body: WsTourWrite }) =>
    unwrapWsWrite<WsTour, WsMeterWarning>((await api.wellServicing.tours.create(jobId, body)).data));
export const useSaveWsTour = () =>
  useWrite(async ({ id, body }: { id: string; body: WsTourWrite }) =>
    unwrapWsWrite<WsTour, WsMeterWarning>((await api.wellServicing.tours.update(id, body)).data));
export const useSubmitWsTour = () =>
  useWrite(async (id: string) => unwrapWs<WsTour>((await api.wellServicing.tours.submit(id)).data));
export const useApproveWsTour = () =>
  useWrite(async (id: string) => unwrapWs<WsTour>((await api.wellServicing.tours.approve(id)).data));
export const useReturnWsTour = () =>
  useWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapWs<WsTour>((await api.wellServicing.tours.returnToDraft(id, reason)).data));
export const useDeleteWsTour = () => useWrite(async (id: string) => (await api.wellServicing.tours.remove(id)).data);

// ─── The field hand (snake_case) ─────────────────────────────────────────────

const EMPTY_FIELD_META: WsFieldMeta = { unit_system: null, units: null, currency: null, country: null, time_zone: null, today: null };

export interface WsFieldResult<T, W = never> {
  data: T | null;
  meta: WsFieldMeta;
  warnings: W[];
}

/** The field envelope: data, snake meta, warnings. `data: null` stays null. */
export function unwrapWsField<T, W = never>(body: unknown): WsFieldResult<T, W> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_FIELD_META, warnings: [] };
  const b = body as { data?: unknown; meta?: Partial<WsFieldMeta>; warnings?: unknown };
  return {
    data: b.data !== undefined && b.data !== null ? (b.data as T) : null,
    meta: { ...EMPTY_FIELD_META, ...(b.meta ?? {}) },
    warnings: Array.isArray(b.warnings) ? (b.warnings as W[]) : [],
  };
}

export function useWsFieldMyTours() {
  return useQuery({
    queryKey: wsJobKeys.fieldMyTours(),
    queryFn: async () => {
      const r = unwrapWsField<WsFieldMyTours>((await api.wellServicing.field.myTours()).data);
      const d = r.data;
      return {
        ...r,
        data: { jobs: Array.isArray(d?.jobs) ? d!.jobs : [], tours: Array.isArray(d?.tours) ? d!.tours : [] },
      };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsFieldTour(id: string | undefined) {
  return useQuery({
    queryKey: wsJobKeys.fieldTour(id ?? ''),
    queryFn: async () => unwrapWsField<WsFieldTour>((await api.wellServicing.field.getTour(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSaveWsFieldTour = () =>
  useWrite(async ({ jobId, workDate, body }: { jobId: string; workDate: string; body: WsTourWrite }) =>
    unwrapWsField<WsFieldTour, WsFieldMeterWarning>((await api.wellServicing.field.saveTour(jobId, workDate, body)).data));
export const useSubmitWsFieldTour = () =>
  useWrite(async (id: string) => unwrapWsField<WsFieldTour>((await api.wellServicing.field.submitTour(id)).data));
